import React, { useEffect, useState } from 'react';
import funcionalidadesService, { type FuncionalidadeDTO } from '../services/funcionalidadesService';
import { UI_THEME } from '../ui/theme';
import { notifyError } from '../utils/notify';
import { toUpperText } from '../utils/formatters';

type FuncionalidadeForm = {
  chave: string;
  descricao: string;
  categoria: 'SAUDE' | 'EDUCACAO';
  percentual: string;
};

const EMPTY_FORM: FuncionalidadeForm = {
  chave: '',
  descricao: '',
  categoria: 'SAUDE',
  percentual: '',
};

const CATEGORY_OPTIONS: Array<{ value: FuncionalidadeForm['categoria']; label: string }> = [
  { value: 'SAUDE', label: 'Saúde' },
  { value: 'EDUCACAO', label: 'Educação' },
];

const categoryLabel = (categoria?: string) =>
  categoria === 'EDUCACAO' ? 'Educação' : 'Saúde';

const FuncionalidadesConfigPage: React.FC = () => {
  const [items, setItems] = useState<FuncionalidadeDTO[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<FuncionalidadeDTO | null>(null);
  const [form, setForm] = useState<FuncionalidadeForm>(EMPTY_FORM);

  const isSaas = UI_THEME === 'saas';
  const surfaceClass = 'border-slate-200 bg-white shadow-sm dark:border-slate-700 dark:bg-slate-900/80';
  const heroClass = isSaas
    ? 'border-teal-100 bg-gradient-to-br from-white via-teal-50/70 to-indigo-50 p-6 shadow-sm dark:border-slate-700 dark:from-slate-900 dark:via-slate-900 dark:to-slate-800'
    : 'border-slate-200 bg-gradient-to-br from-white via-slate-50 to-blue-50 p-6 shadow-sm dark:border-slate-700 dark:from-slate-900 dark:via-slate-900 dark:to-slate-800';
  const accentTextClass = isSaas ? 'text-teal-700 dark:text-teal-300' : 'text-blue-700 dark:text-blue-300';
  const bodyTextClass = 'text-slate-600 dark:text-slate-300';
  const labelTextClass = 'text-slate-500 dark:text-slate-400';
  const strongTextClass = 'text-slate-900 dark:text-slate-100';
  const inputClass = 'w-full rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-700 outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-100 disabled:bg-slate-100 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100 dark:disabled:bg-slate-800 dark:focus:ring-blue-900/40';
  const primaryButtonClass = isSaas
    ? 'bg-teal-600 text-white hover:bg-teal-700 dark:bg-teal-500 dark:hover:bg-teal-400'
    : 'bg-blue-700 text-white hover:bg-blue-800 dark:bg-blue-600 dark:hover:bg-blue-500';
  const secondaryButtonClass = 'border border-slate-300 bg-white text-slate-700 hover:bg-slate-50 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800';

  useEffect(() => {
    fetchItems();
  }, []);

  const fetchItems = async () => {
    setLoading(true);
    try {
      const res = await funcionalidadesService.listAll();
      setItems(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error(err);
      notifyError('Erro ao carregar regras');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setEditing(null);
    setForm(EMPTY_FORM);
  };

  const totalPercentualAtivoAtual = items
    .filter((item) => item.ativo)
    .reduce((sum, item) => sum + Number(item.percentual || 0), 0);

  const totalPercentualProjetado = totalPercentualAtivoAtual
    - (editing?.ativo ? Number(editing.percentual || 0) : 0)
    + Number(form.percentual || 0);

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();

    if (totalPercentualProjetado > 100) {
      notifyError('A soma dos percentuais ativos não pode ultrapassar 100%');
      return;
    }

    try {
      const payload = {
        chave: form.chave.trim(),
        descricao: form.descricao.trim(),
        categoria: form.categoria,
        percentual: Number(form.percentual),
      };

      if (editing) {
        await funcionalidadesService.update(editing.id, {
          descricao: payload.descricao,
          categoria: payload.categoria,
          percentual: payload.percentual,
        });
      } else {
        await funcionalidadesService.create(payload);
      }

      await fetchItems();
      resetForm();
    } catch (err: any) {
      notifyError(err?.response?.data?.error || 'Erro ao salvar');
    }
  };

  const startEdit = (item: FuncionalidadeDTO) => {
    setEditing(item);
    setForm({
      chave: item.chave,
      descricao: item.descricao || '',
      categoria: item.categoria,
      percentual: String(item.percentual ?? ''),
    });
  };

  const handleDelete = async (id: number) => {
    if (!confirm('Excluir regra?')) return;
    try {
      await funcionalidadesService.remove(id);
      await fetchItems();
      if (editing?.id === id) resetForm();
    } catch {
      notifyError('Erro ao excluir');
    }
  };

  const summaryCards = [
    { label: 'Regras cadastradas', value: items.length, dot: 'bg-slate-400' },
    { label: 'Regras ativas', value: items.filter((item) => item.ativo).length, dot: isSaas ? 'bg-teal-500' : 'bg-blue-600' },
    { label: 'Percentual ativo', value: `${Number(totalPercentualAtivoAtual).toFixed(2)}%`, dot: 'bg-emerald-500' },
    { label: 'Percentual projetado', value: `${Number(totalPercentualProjetado || 0).toFixed(2)}%`, dot: totalPercentualProjetado > 100 ? 'bg-red-500' : 'bg-amber-500' },
  ];

  return (
    <div className="w-full space-y-6">
      <section className={`rounded-3xl border ${heroClass}`}>
        <p className={`text-xs font-semibold uppercase tracking-[0.2em] ${accentTextClass}`}>
          {isSaas ? 'Configurações' : 'Administrador'}
        </p>
        <div className="mt-2 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <h1 className={`text-2xl font-bold tracking-tight sm:text-3xl ${strongTextClass}`}>Funcionalidades</h1>
            <p className={`mt-2 text-sm leading-6 ${bodyTextClass}`}>
              Cadastre uma regra por linha usando a mesma chave da função ou área de atuação da emenda.
            </p>
          </div>
        </div>

        <div className={`mt-5 overflow-hidden rounded-2xl border ${surfaceClass}`}>
          <div className="grid divide-y divide-slate-200 dark:divide-slate-700 sm:grid-cols-2 sm:divide-x sm:divide-y-0 xl:grid-cols-4">
            {summaryCards.map((card) => (
              <div key={card.label} className="min-h-[72px] px-4 py-3">
                <p className={`flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.18em] ${labelTextClass}`}>
                  <span className={`h-1.5 w-1.5 rounded-full ${card.dot}`} />
                  {card.label}
                </p>
                <p className={`mt-2 text-2xl font-bold ${strongTextClass}`}>{card.value}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className={`rounded-3xl border p-5 ${surfaceClass}`}>
        <form onSubmit={handleSave} className="grid grid-cols-1 gap-3 md:grid-cols-4">
          <div>
            <label className={`mb-1 block text-xs font-semibold ${labelTextClass}`}>Função / área de atuação</label>
            <input
              required
              disabled={!!editing}
              value={form.chave}
              onChange={(e) => setForm((current) => ({ ...current, chave: toUpperText(e.target.value) }))}
              className={inputClass}
              placeholder="Código da função / área"
            />
          </div>

          <div>
            <label className={`mb-1 block text-xs font-semibold ${labelTextClass}`}>Descrição</label>
            <input
              value={form.descricao}
              onChange={(e) => setForm((current) => ({ ...current, descricao: toUpperText(e.target.value) }))}
              className={inputClass}
            />
          </div>

          <div>
            <label className={`mb-1 block text-xs font-semibold ${labelTextClass}`}>Categoria</label>
            <select
              value={form.categoria}
              onChange={(e) => setForm((current) => ({ ...current, categoria: e.target.value as FuncionalidadeForm['categoria'] }))}
              className={inputClass}
            >
              {CATEGORY_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className={`mb-1 block text-xs font-semibold ${labelTextClass}`}>Percentual</label>
            <input
              required
              min="0"
              max="100"
              type="number"
              step="0.01"
              value={form.percentual}
              onChange={(e) => setForm((current) => ({ ...current, percentual: e.target.value }))}
              className={inputClass}
            />
          </div>

          <div className="flex gap-2 pt-1 md:col-span-4">
            <button
              type="submit"
              className={`rounded-xl px-4 py-2 text-sm font-semibold transition ${primaryButtonClass}`}
            >
              {editing ? 'Salvar' : 'Criar'}
            </button>
            {editing && (
              <button
                type="button"
                onClick={resetForm}
                className={`rounded-xl px-4 py-2 text-sm font-medium transition ${secondaryButtonClass}`}
              >
                Cancelar
              </button>
            )}
          </div>
        </form>
      </section>

      <section className={`overflow-hidden rounded-3xl border ${surfaceClass}`}>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-slate-200 dark:divide-slate-800">
            <thead className="bg-slate-50 dark:bg-slate-800/80">
              <tr>
                <th className={`px-6 py-3 text-left text-xs font-semibold uppercase tracking-[0.12em] ${labelTextClass}`}>
                  Função / área
                </th>
                <th className={`px-6 py-3 text-left text-xs font-semibold uppercase tracking-[0.12em] ${labelTextClass}`}>
                  Descrição
                </th>
                <th className={`px-6 py-3 text-left text-xs font-semibold uppercase tracking-[0.12em] ${labelTextClass}`}>
                  Categoria
                </th>
                <th className={`px-6 py-3 text-center text-xs font-semibold uppercase tracking-[0.12em] ${labelTextClass}`}>
                  Percentual
                </th>
                <th className={`px-6 py-3 text-center text-xs font-semibold uppercase tracking-[0.12em] ${labelTextClass}`}>
                  Ações
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200 bg-white dark:divide-slate-800 dark:bg-slate-900">
              {loading ? (
                <tr>
                  <td colSpan={5} className={`px-6 py-10 text-center text-sm ${labelTextClass}`}>
                    Carregando regras...
                  </td>
                </tr>
              ) : items.length === 0 ? (
                <tr>
                  <td colSpan={5} className={`px-6 py-10 text-center text-sm ${labelTextClass}`}>
                    Nenhuma regra cadastrada.
                  </td>
                </tr>
              ) : (
                items.map((item) => (
                  <tr key={item.id} className="hover:bg-slate-50 dark:hover:bg-slate-800/60">
                    <td className={`px-6 py-4 text-sm font-medium ${strongTextClass}`}>{item.chave}</td>
                    <td className={`px-6 py-4 text-sm ${bodyTextClass}`}>{item.descricao || '-'}</td>
                    <td className={`px-6 py-4 text-sm ${bodyTextClass}`}>{categoryLabel(item.categoria)}</td>
                    <td className={`px-6 py-4 text-center text-sm ${strongTextClass}`}>
                      {Number(item.percentual || 0).toFixed(2)}%
                    </td>
                    <td className="px-6 py-4 text-center text-sm">
                      <button
                        type="button"
                        onClick={() => startEdit(item)}
                        className={`mr-3 font-medium ${accentTextClass}`}
                      >
                        Editar
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDelete(item.id)}
                        className="font-medium text-red-600 hover:text-red-800 dark:text-red-300 dark:hover:text-red-200"
                      >
                        Excluir
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
};

export default FuncionalidadesConfigPage;
